import { ComboboxProps, SelectOption } from '@chronosphereio/chrono-ui';
import { useEffect, useMemo } from 'react';
import { VariableOption } from '../../../core';
import { VariableCombobox } from './VariableCombobox';
import { useTemplateVariableOptions } from './variable-options';

export interface SingleSelectVariableProps {
  title: string;
  options?: VariableOption[];
  isLoading: boolean;
  allowAllValue: boolean;
  value: string | null;
  hasLoaded: boolean;
  onChange: (nextValue: string) => void;
  disabled?: boolean;
  renderFooter?: ComboboxProps['renderFooter'];
}

/**
 * Input for selecting a single variable value from a list of options.
 */
export function SingleSelectVariable(props: SingleSelectVariableProps) {
  const { title, options, isLoading, allowAllValue, value, onChange, disabled } = props;

  const { optionsByKey, selectOptions, hasLoadedOptions } = useTemplateVariableOptions(allowAllValue, options);

  const selectValue = useMemo(() => {
    // If the value is null (not sure when this could happen?), return a dummy placeholder option so that we don't pass
    // undefined and switch the input between being uncontrolled and controlled
    if (value === null) return NO_VALUE;

    const found = optionsByKey.get(value);
    if (found !== undefined) return found;

    // If not found, return a dummy placeholder option based on the value (TODO: Is this what we want?)
    return { key: value, label: value };
  }, [optionsByKey, value]);

  useEffect(() => {
    // Don't change values while loading
    if (isLoading) return;

    // If we don't have a selection, but we have options, select the first option
    if (selectValue === NO_VALUE && selectOptions.length > 0 && selectOptions[0] !== undefined) {
      onChange(selectOptions[0].key);
    }
  }, [selectOptions, selectValue, onChange, isLoading, hasLoadedOptions]);

  const comboboxOptions = useMemo(() => {
    return selectOptions.map((d) => ({
      label: d.label,
      value: d.key,
    }));
  }, [selectOptions]);

  return (
    <VariableCombobox
      label={title}
      options={comboboxOptions}
      isLoading={isLoading}
      disabled={disabled}
      value={selectValue !== NO_VALUE ? selectValue.key : null}
      onSelect={(e) => {
        onChange(e as unknown as string);
      }}
      renderFooter={props.renderFooter}
    />
  );
}

const NO_VALUE: SelectOption = { key: '__null__', label: '' };
